import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getCourse, validateCoupon } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { useRazorpay } from 'react-razorpay';
import axios from 'axios';
import {
  ArrowLeft, Check, Shield, Award, Clock, BookOpen,
  GraduationCap, IndianRupee, Percent, Sparkles, CreditCard,
  Lock, CheckCircle, AlertCircle, Mail, Phone, User,
  FileText, Download, Loader2, Copy
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generate PDF Receipt Function
const generateReceiptPDF = (receipt, course) => {
  const VIA_LOGO_URL = "https://customer-assets.emergentagent.com/job_via-platform/artifacts/epc7ik46_VIA_LOGO_Tranparent.png";
  
  const formatAmount = (amount) => {
    if (!amount && amount !== 0) return '₹0';
    return `₹${Number(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const paymentDate = new Date().toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  // Create printable HTML content
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Receipt - ${receipt.invoice_number}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
        .header { text-align: center; border-bottom: 3px solid #10b981; padding-bottom: 25px; margin-bottom: 30px; }
        .logo { height: 60px; margin-bottom: 10px; }
        .company-name { font-size: 20px; font-weight: bold; color: #1f2937; margin: 10px 0 5px 0; }
        .invoice-title { font-size: 24px; color: #10b981; margin-top: 15px; font-weight: bold; }
        .info-row { display: flex; justify-content: space-between; margin-bottom: 10px; padding: 8px 0; border-bottom: 1px solid #f3f4f6; }
        .section { margin-bottom: 30px; }
        .section-title { font-size: 14px; font-weight: bold; color: #10b981; margin-bottom: 15px; text-transform: uppercase; letter-spacing: 1px; }
        .amount-table { width: 100%; border-collapse: collapse; }
        .amount-table td { padding: 12px 0; }
        .amount-table .label { color: #6b7280; }
        .amount-table .value { text-align: right; color: #1f2937; }
        .amount-table .total { font-weight: bold; font-size: 18px; border-top: 2px solid #10b981; }
        .total .value { color: #10b981; }
        .gst-section { background: #f0fdf4; padding: 15px; border-radius: 8px; margin-top: 20px; border: 1px solid #d1fae5; }
        .footer { text-align: center; margin-top: 50px; padding-top: 25px; border-top: 2px solid #e5e7eb; color: #6b7280; font-size: 12px; }
        .footer-logo { height: 40px; opacity: 0.7; margin-bottom: 10px; }
        @media print { 
          body { padding: 20px; }
          .header { border-bottom-color: #10b981 !important; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <img src="${VIA_LOGO_URL}" alt="VIA Logo" class="logo" />
        <div class="company-name">Visual Institute of Arts</div>
        <div class="invoice-title">PAYMENT RECEIPT</div>
      </div>
      
      <div class="section">
        <div class="section-title">Invoice Details</div>
        <div class="info-row"><span>Invoice Number:</span><strong>${receipt.invoice_number}</strong></div>
        <div class="info-row"><span>Date:</span><span>${paymentDate}</span></div>
      </div>
      
      <div class="section">
        <div class="section-title">Course Details</div>
        <div class="info-row"><span>Course:</span><span>${course?.title || 'N/A'}</span></div>
      </div>
      
      <div class="section">
        <div class="section-title">Payment Summary</div>
        <table class="amount-table">
          <tr><td class="label">Base Amount</td><td class="value">${formatAmount(receipt.gst_breakdown?.base_amount)}</td></tr>
          <tr><td class="label">CGST (9%)</td><td class="value">${formatAmount(receipt.gst_breakdown?.cgst)}</td></tr>
          <tr><td class="label">SGST (9%)</td><td class="value">${formatAmount(receipt.gst_breakdown?.sgst)}</td></tr>
          <tr class="total"><td class="label">Total Paid</td><td class="value">${formatAmount(receipt.gst_breakdown?.total)}</td></tr>
        </table>
        <div class="gst-section">
          <small>GST @ 18% (CGST 9% + SGST 9%) is included in the above amount as per GST Act.</small>
        </div>
      </div>
      
      <div class="footer">
        <img src="${VIA_LOGO_URL}" alt="VIA" class="footer-logo" />
        <p>Thank you for choosing Visual Institute of Arts!</p>
        <p>For any queries, contact: admissions@visual-institute-of-arts.com</p>
      </div>
    </body>
    </html>
  `;

  // Open in new window for printing/saving
  const printWindow = window.open('', '_blank');
  printWindow.document.write(htmlContent);
  printWindow.document.close();
  printWindow.print();
};

export default function Checkout() {
  const { courseId } = useParams();
  const [searchParams] = useSearchParams();
  const tier = searchParams.get('tier') || 'self_learning';
  const couponFromUrl = searchParams.get('coupon') || '';
  
  const { user, isAuthenticated } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { Razorpay, isLoading: razorpayLoading } = useRazorpay();
  
  const [course, setCourse] = useState(null);
  const [pricingTiers, setPricingTiers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  
  // Guest checkout form
  const [guestForm, setGuestForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: ''
  });
  const [formErrors, setFormErrors] = useState({});
  
  // Coupon
  const [couponCode, setCouponCode] = useState(couponFromUrl);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  
  // Payment result
  const [paymentResult, setPaymentResult] = useState(null);

  useEffect(() => {
    loadData();
  }, [courseId]);

  useEffect(() => {
    // Pre-fill form for logged in users
    if (user) {
      setGuestForm(prev => ({
        ...prev,
        name: user.name || prev.name,
        email: user.email || prev.email
      }));
    }
  }, [user]);

  // Auto-apply coupon from URL
  useEffect(() => {
    if (couponFromUrl && pricingTiers && !appliedCoupon) {
      handleValidateCoupon();
    }
  }, [couponFromUrl, pricingTiers]);

  const loadData = async () => {
    try {
      const [courseRes, pricingRes] = await Promise.all([
        getCourse(courseId),
        axios.get(`${API}/courses/${courseId}/pricing`)
      ]);
      setCourse(courseRes.data);
      setPricingTiers(pricingRes.data.pricing_tiers);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load course information');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!guestForm.name.trim() || guestForm.name.length < 2) {
      errors.name = 'Please enter your full name';
    }
    if (!guestForm.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestForm.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!guestForm.phone.trim() || guestForm.phone.length < 10) {
      errors.phone = 'Please enter a valid phone number';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    const selectedTier = tier === 'instructor_led' ? pricingTiers?.instructor_led : pricingTiers?.self_learning;
    if (!selectedTier) return;

    setValidatingCoupon(true);
    try {
      const response = await validateCoupon(couponCode, courseId, tier, selectedTier.price);
      setAppliedCoupon(response.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid coupon code');
      setAppliedCoupon(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handlePayment = async () => {
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setProcessing(true);

    try {
      // Get payment config
      const configRes = await axios.get(`${API}/payments/config`);
      const config = configRes.data;

      // Create guest checkout session
      const checkoutRes = await axios.post(`${API}/payments/guest-checkout`, {
        name: guestForm.name,
        email: guestForm.email,
        phone: guestForm.phone,
        course_id: courseId,
        tier: tier,
        coupon_code: appliedCoupon?.code || null
      });

      const checkoutData = checkoutRes.data;

      // Handle mock mode
      if (config.mock_mode || checkoutData.mock_mode) {
        const confirmMock = window.confirm(
          `🧪 MOCK PAYMENT MODE\n\n` +
          `Course: ${checkoutData.course.title}\n` +
          `Amount: ₹${(checkoutData.amount / 100).toLocaleString('en-IN')}\n` +
          `GST (18%): ₹${(checkoutData.gst_breakdown.gst_amount / 100).toLocaleString('en-IN')}\n\n` +
          `Click OK to simulate successful payment\n` +
          `Click Cancel to cancel`
        );

        if (confirmMock) {
          // Verify mock payment
          const verifyRes = await axios.post(`${API}/payments/guest-verify`, {
            razorpay_order_id: checkoutData.razorpay_order_id,
            razorpay_payment_id: `pay_mock_${Date.now()}`,
            razorpay_signature: `mock_sig_${Date.now()}`,
            checkout_id: checkoutData.checkout_id
          });

          setPaymentResult(verifyRes.data);
          toast.success('Payment successful!');
        } else {
          setProcessing(false);
          return;
        }
      } else {
        // Real Razorpay payment
        const options = {
          key: config.key_id,
          amount: checkoutData.amount,
          currency: 'INR',
          name: config.name,
          description: `Enrollment: ${checkoutData.course.title}`,
          image: config.image,
          order_id: checkoutData.razorpay_order_id,
          handler: async (response) => {
            try {
              const verifyRes = await axios.post(`${API}/payments/guest-verify`, {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                checkout_id: checkoutData.checkout_id
              });

              setPaymentResult(verifyRes.data);
              toast.success('Payment successful!');
            } catch (error) {
              toast.error('Payment verification failed');
              setProcessing(false);
            }
          },
          prefill: checkoutData.prefill,
          theme: config.theme,
          modal: {
            ondismiss: () => {
              setProcessing(false);
            }
          }
        };

        const razorpayInstance = new Razorpay(options);
        razorpayInstance.on('payment.failed', (response) => {
          toast.error(`Payment failed: ${response.error.description}`);
          setProcessing(false);
        });
        razorpayInstance.open();
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process payment');
      setProcessing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const cardClass = theme === 'dark' ? 'card-shadow-dark' : 'card-shadow-light';

  const selectedTier = tier === 'instructor_led' ? pricingTiers?.instructor_led : pricingTiers?.self_learning;
  const originalPrice = selectedTier?.price || 0;
  const discountAmount = appliedCoupon?.discount_amount || 0;
  const finalPrice = appliedCoupon?.final_price || originalPrice;

  // Calculate GST breakdown
  const gstRate = 0.18;
  const baseAmount = Math.round(finalPrice / (1 + gstRate));
  const gstAmount = finalPrice - baseAmount;

  if (loading) {
    return (
      <div className="min-h-screen pt-20 pb-12">
        <div className="max-w-5xl mx-auto px-4">
          <Skeleton className="h-8 w-48 mb-6" />
          <div className="grid md:grid-cols-5 gap-8">
            <div className="md:col-span-3 space-y-6">
              <Skeleton className="h-[200px] rounded-2xl" />
              <Skeleton className="h-[300px] rounded-2xl" />
            </div>
            <div className="md:col-span-2">
              <Skeleton className="h-[400px] rounded-2xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!course || !selectedTier) {
    return (
      <div className="min-h-screen pt-20 pb-12 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Course or pricing not found</h1>
          <Button onClick={() => navigate('/courses')}>Browse Courses</Button>
        </div>
      </div>
    );
  }

  // Payment Success Screen
  if (paymentResult) {
    return (
      <div className="min-h-screen pt-20 pb-12" data-testid="payment-success">
        <div className="max-w-2xl mx-auto px-4">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="text-center mb-8"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="text-3xl font-bold mb-2">Payment Successful!</h1>
            <p className="text-muted-foreground">
              {paymentResult.is_new_user 
                ? "Your account has been created. Check your email for login details."
                : "Course has been added to your account."}
            </p>
          </motion.div>

          {/* New User Credentials */}
          {paymentResult.is_new_user && paymentResult.temp_password && (
            <Card className={`${cardClass} mb-6 border-emerald-500/50`}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-600">
                  <Lock className="w-5 h-5" />
                  Your Login Credentials
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Email:</span>
                    <div className="flex items-center gap-2">
                      <code className="bg-background px-2 py-1 rounded text-sm">{paymentResult.user.email}</code>
                      <Button size="icon" variant="ghost" onClick={() => copyToClipboard(paymentResult.user.email)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Temporary Password:</span>
                    <div className="flex items-center gap-2">
                      <code className="bg-background px-2 py-1 rounded text-sm font-mono">{paymentResult.temp_password}</code>
                      <Button size="icon" variant="ghost" onClick={() => copyToClipboard(paymentResult.temp_password)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Please save these credentials and change your password after first login.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Receipt Summary */}
          <Card className={cardClass}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Payment Receipt
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center pb-4 border-b">
                <span className="text-muted-foreground">Invoice Number</span>
                <span className="font-mono font-semibold">{paymentResult.receipt.invoice_number}</span>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Course</span>
                  <span>{paymentResult.course.title}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Base Amount</span>
                  <span>₹{paymentResult.receipt.gst_breakdown.base_amount.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>CGST (9%)</span>
                  <span>₹{paymentResult.receipt.gst_breakdown.cgst.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>SGST (9%)</span>
                  <span>₹{paymentResult.receipt.gst_breakdown.sgst.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between font-semibold pt-2 border-t">
                  <span>Total Paid</span>
                  <span className="text-emerald-600">₹{paymentResult.receipt.gst_breakdown.total.toLocaleString('en-IN')}</span>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  className="flex-1"
                  onClick={() => navigate('/login')}
                >
                  Login to Dashboard
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => generateReceiptPDF(paymentResult.receipt, paymentResult.course)}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Receipt
                </Button>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-muted-foreground mt-6">
            A confirmation email with your receipt has been sent to {paymentResult.user.email}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="checkout-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(`/courses/${courseId}`)}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Course
        </Button>

        {/* Header */}
        <div className="mb-8">
          <Badge variant="outline" className="mb-3">
            <Lock className="w-3 h-3 mr-1" />
            Secure Checkout
          </Badge>
          <h1 className="text-3xl font-bold mb-2">Complete Your Enrollment</h1>
          <p className="text-muted-foreground">
            {isAuthenticated 
              ? "Complete payment to access your course instantly."
              : "Fill in your details to create your account and access the course."}
          </p>
        </div>

        <div className="grid md:grid-cols-5 gap-8">
          {/* Left Column - Form */}
          <div className="md:col-span-3 space-y-6">
            {/* Contact Details */}
            <Card className={cardClass}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Your Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    placeholder="Enter your full name"
                    value={guestForm.name}
                    onChange={(e) => setGuestForm({ ...guestForm, name: e.target.value })}
                    className={formErrors.name ? 'border-red-500' : ''}
                    disabled={processing}
                  />
                  {formErrors.name && <p className="text-xs text-red-500">{formErrors.name}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={guestForm.email}
                    onChange={(e) => setGuestForm({ ...guestForm, email: e.target.value })}
                    className={formErrors.email ? 'border-red-500' : ''}
                    disabled={processing || isAuthenticated}
                  />
                  {formErrors.email && <p className="text-xs text-red-500">{formErrors.email}</p>}
                  {!isAuthenticated && (
                    <p className="text-xs text-muted-foreground">
                      Your login credentials will be sent to this email
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="Enter your phone number"
                    value={guestForm.phone}
                    onChange={(e) => setGuestForm({ ...guestForm, phone: e.target.value })}
                    className={formErrors.phone ? 'border-red-500' : ''}
                    disabled={processing}
                  />
                  {formErrors.phone && <p className="text-xs text-red-500">{formErrors.phone}</p>}
                </div>
              </CardContent>
            </Card>

            {/* Course Summary */}
            <Card className={cardClass}>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Course Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4">
                  <img
                    src={course.thumbnail_url || 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=200'}
                    alt={course.title}
                    className="w-24 h-24 rounded-lg object-cover"
                  />
                  <div>
                    <h3 className="font-semibold text-lg">{course.title}</h3>
                    <Badge className="mt-1" variant="outline">
                      {tier === 'instructor_led' ? (
                        <><GraduationCap className="w-3 h-3 mr-1" /> Instructor-Led</>
                      ) : (
                        <><BookOpen className="w-3 h-3 mr-1" /> Self-Learning</>
                      )}
                    </Badge>
                    <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {course.duration}
                      </span>
                      <span className="flex items-center gap-1">
                        <BookOpen className="w-4 h-4" />
                        {course.modules?.length || 0} Modules
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Coupon */}
            <Card className={cardClass}>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Percent className="w-5 h-5" />
                  Have a Coupon?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    disabled={!!appliedCoupon || processing}
                    className="flex-1"
                  />
                  {appliedCoupon ? (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setAppliedCoupon(null);
                        setCouponCode('');
                      }}
                      disabled={processing}
                    >
                      Remove
                    </Button>
                  ) : (
                    <Button
                      onClick={handleValidateCoupon}
                      disabled={validatingCoupon || !couponCode.trim() || processing}
                    >
                      {validatingCoupon ? 'Validating...' : 'Apply'}
                    </Button>
                  )}
                </div>
                {appliedCoupon && (
                  <div className="mt-3 p-3 bg-green-500/10 rounded-lg flex items-center gap-2 text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm">
                      Coupon "{appliedCoupon.code}" applied! You save ₹{discountAmount.toLocaleString('en-IN')}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Payment Summary */}
          <div className="md:col-span-2">
            <Card className={`${cardClass} sticky top-24`}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Order Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Price Breakdown with GST */}
                <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Course Price</span>
                    <span>₹{originalPrice.toLocaleString('en-IN')}</span>
                  </div>
                  
                  {discountAmount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Discount {appliedCoupon?.code && `(${appliedCoupon.code})`}</span>
                      <span>-₹{discountAmount.toLocaleString('en-IN')}</span>
                    </div>
                  )}

                  <div className="border-t border-border pt-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span>₹{baseAmount.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">CGST (9%)</span>
                      <span>₹{Math.round(gstAmount / 2).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">SGST (9%)</span>
                      <span>₹{(gstAmount - Math.round(gstAmount / 2)).toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                  
                  <div className="border-t border-border pt-3">
                    <div className="flex justify-between font-semibold text-lg">
                      <span>Total</span>
                      <span className="text-primary">₹{finalPrice.toLocaleString('en-IN')}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      (Inclusive of 18% GST)
                    </p>
                  </div>
                </div>

                {/* Pay Button */}
                <Button
                  className="w-full h-12 text-base font-semibold bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                  onClick={handlePayment}
                  disabled={processing}
                  data-testid="pay-button"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Pay ₹{finalPrice.toLocaleString('en-IN')}
                    </>
                  )}
                </Button>

                {/* Trust Badges */}
                <div className="flex items-center justify-center gap-4 pt-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Shield className="w-3.5 h-3.5" />
                    Secure Payment
                  </span>
                  <span className="flex items-center gap-1">
                    <Award className="w-3.5 h-3.5" />
                    7-Day Refund
                  </span>
                </div>

                {/* Payment Methods */}
                <div className="border-t pt-4">
                  <p className="text-xs text-muted-foreground text-center mb-2">Accepted Payment Methods</p>
                  <div className="flex justify-center gap-4 text-muted-foreground">
                    <span className="text-xs">UPI</span>
                    <span className="text-xs">Cards</span>
                    <span className="text-xs">Net Banking</span>
                    <span className="text-xs">Wallets</span>
                  </div>
                </div>

                {/* Already have account */}
                {!isAuthenticated && (
                  <div className="text-center pt-2">
                    <p className="text-sm text-muted-foreground">
                      Already have an account?{' '}
                      <Button 
                        variant="link" 
                        className="p-0 h-auto text-primary"
                        onClick={() => navigate('/login', { state: { from: `/checkout/${courseId}?tier=${tier}` } })}
                      >
                        Login here
                      </Button>
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
